import type { SelectionInfo } from '@astra/shared';
import { SpaceEntity } from './SpaceEntity';

/**
 * TargetableEntity - Can be selected/targeted by the player.
 *
 * Extends: SpaceEntity
 *
 * Provides:
 * - Selection detection (right-click)
 * - isHostile flag for targeting UI
 * - getSelectionInfo() for HUD panel
 *
 * Examples: Station (selection only), Gateway (selection only)
 */
export abstract class TargetableEntity extends SpaceEntity {
  /** Radius for right-click selection detection (usually larger than collision) */
  abstract readonly selectionRadius: number;

  /** Whether this entity appears as hostile (affects targeting UI color) */
  abstract readonly isHostile: boolean;

  /**
   * Check if a world point is within selection range.
   * Used for right-click target detection.
   */
  isPointInSelectionRange(worldX: number, worldY: number): boolean {
    const dx = worldX - this.x;
    const dy = worldY - this.y;
    return dx * dx + dy * dy <= this.selectionRadius * this.selectionRadius;
  }

  /**
   * Get selection panel data when this entity is right-clicked.
   * Each entity type defines what info to show in the HUD.
   */
  abstract getSelectionInfo(): SelectionInfo;
}
